<template>
  <section class="lead-conversation-group">
    <header class="lead-conversation-group__heading">
      <div>
        <strong>{{ groupTitle }}</strong>
        <small
          v-if="providerNames"
          v-tooltip="{ content: providerDetails, placement: 'top', offset: 8 }"
        >
          {{ providerNames }}
        </small>
      </div>
      <span>
        {{ messageCount }} {{ messageCount === 1 ? 'message' : 'messages' }}
        <i v-if="failedCount">{{ failedCount }} failed</i>
      </span>
    </header>

    <div class="lead-conversation-group__messages">
      <button
        v-if="hasMore && conversationId"
        class="lead-conversation-group__more"
        type="button"
        :disabled="loadingMore"
        @click="loadOlderMessages"
      >
        {{ loadingMore ? 'Loading earlier messages...' : `Load ${hiddenMessageCount} earlier ${hiddenMessageCount === 1 ? 'message' : 'messages'}` }}
      </button>
      <p v-if="error" class="lead-conversation-group__error">
        Earlier messages could not be loaded.
        <button type="button" @click="loadOlderMessages">Retry</button>
      </p>
      <LeadCommunicationMessage
        v-for="message in messages"
        :key="message.id"
        :communication="message"
        :pending="Boolean(pendingIds[message.id])"
        :hide-subject="messageRepeatsGroupTitle(message)"
        @edit="$emit('edit', $event)"
        @delete="$emit('delete', $event)"
      />
      <p v-if="!messages.length" class="lead-conversation-group__empty">
        {{ item.summary && item.summary.latest_message_preview ? item.summary.latest_message_preview : 'No messages loaded' }}
      </p>
    </div>
  </section>
</template>

<script>
import { getLeadFlowConversationMessages } from '@/http'
import LeadCommunicationMessage from '@/views/crm/task-tracker/LeadCommunicationMessage.vue'

export default {
  name: 'LeadConversationGroup',
  components: {
    LeadCommunicationMessage
  },
  props: {
    leadId: {
      type: Number,
      required: true
    },
    item: {
      type: Object,
      required: true
    },
    pendingIds: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    const messages = this.item.messages || {}
    return {
      messages: Array.isArray(messages.results) ? [...messages.results] : [],
      nextCursor: messages.next_cursor || null,
      hasMore: Boolean(messages.has_more && messages.next_cursor),
      loadingMore: false,
      error: null,
      requestGeneration: 0
    }
  },
  computed: {
    group() {
      return this.item.group || {}
    },
    groupTitle() {
      return this.group.subject || 'Conversation'
    },
    providerNames() {
      return (this.group.providers || []).map(provider => provider.name).filter(Boolean).join(' / ')
    },
    providerDetails() {
      return (this.group.providers || []).map(provider => {
        const details = [
          provider.name || provider.code,
          provider.thread_id ? `Thread: ${provider.thread_id}` : '',
          provider.ticket_id ? `Ticket: ${provider.ticket_id}` : ''
        ].filter(Boolean)
        return details.join(' · ')
      }).filter(Boolean).join('\n')
    },
    conversationId() {
      return this.group.conversation_id || null
    },
    messageCount() {
      return Number(this.item.summary && this.item.summary.message_count) || this.messages.length
    },
    hiddenMessageCount() {
      return Math.max(this.messageCount - this.messages.length, 0)
    },
    failedCount() {
      return Number(this.item.summary && this.item.summary.failed_count) || 0
    }
  },
  beforeDestroy() {
    this.requestGeneration += 1
  },
  methods: {
    messageRepeatsGroupTitle(message) {
      const messageSubject = String(message && message.subject ? message.subject : '').trim()
      const groupSubject = String(this.group.subject || '').trim()
      return Boolean(messageSubject && groupSubject && messageSubject === groupSubject)
    },
    async loadOlderMessages() {
      if (!this.conversationId || this.loadingMore || !this.hasMore || !this.nextCursor) return
      const scrollContainer = this.$el.closest('.lead-communication-history-drawer__content')
      const previousScrollHeight = scrollContainer ? scrollContainer.scrollHeight : 0
      const previousScrollTop = scrollContainer ? scrollContainer.scrollTop : 0
      const generation = this.requestGeneration + 1
      this.requestGeneration = generation
      this.loadingMore = true
      this.error = null
      try {
        const { data } = await getLeadFlowConversationMessages(
          this.leadId,
          this.conversationId,
          {
            page_size: 20,
            cursor: this.nextCursor
          }
        )
        if (generation !== this.requestGeneration) return
        const loadedIds = new Set(this.messages.map(message => message.id))
        const olderMessages = (data.results || []).filter(message => !loadedIds.has(message.id))
        this.messages = olderMessages.concat(this.messages)
        this.nextCursor = data.next_cursor || null
        this.hasMore = Boolean(data.has_more && this.nextCursor)
        this.$nextTick(() => {
          if (!scrollContainer) return
          scrollContainer.scrollTop = previousScrollTop + scrollContainer.scrollHeight - previousScrollHeight
        })
      } catch (error) {
        if (generation === this.requestGeneration) this.error = error
      } finally {
        if (generation === this.requestGeneration) this.loadingMore = false
      }
    }
  }
}
</script>

<style scoped>
.lead-conversation-group {
  padding: 12px;
  border: 1px solid #dfe4dd;
  border-radius: 6px;
  background: #fff;
}

.lead-conversation-group__heading {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding-bottom: 9px;
  border-bottom: 1px solid #edf0ec;
}

.lead-conversation-group__heading > div {
  display: grid;
  min-width: 0;
  gap: 2px;
}

.lead-conversation-group__heading strong {
  overflow: hidden;
  color: #344749;
  font-size: 11px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.lead-conversation-group__heading small,
.lead-conversation-group__heading > span {
  color: #899593;
  font-size: 9px;
}

.lead-conversation-group__heading > span {
  flex: 0 0 auto;
  text-align: right;
}

.lead-conversation-group__heading i {
  display: block;
  margin-top: 3px;
  color: #b86a45;
  font-style: normal;
}

.lead-conversation-group__messages {
  display: grid;
  gap: 8px;
  margin-top: 10px;
}

.lead-conversation-group__empty {
  margin: 0;
  color: #8b9694;
  font-size: 10px;
}

.lead-conversation-group__more {
  min-height: 34px;
  padding: 0 12px;
  border: 1px solid #b9d2f4;
  border-radius: 6px;
  background: #f4f8fe;
  color: #1769e0;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
}

.lead-conversation-group__more:hover {
  border-color: #8eb8ef;
  background: #eaf2fd;
}

.lead-conversation-group__more:disabled {
  cursor: wait;
  opacity: .65;
}

.lead-conversation-group__error {
  margin: 0;
  color: #b85049;
  font-size: 10px;
  text-align: center;
}

.lead-conversation-group__error button {
  padding: 0;
  border: 0;
  background: transparent;
  color: inherit;
  font-weight: 700;
  cursor: pointer;
}
</style>
